import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import type { AuthMode } from '../auth-dialog-context';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { LoginForm } from './LoginForm';
import { RegisterForm } from './RegisterForm';

interface AuthDialogProps {
  mode: AuthMode;
  onModeChange: (mode: AuthMode) => void;
  onClose: () => void;
  onAuthenticated: () => void;
}

const titles: Record<AuthMode, { title: string; subtitle: string }> = {
  login: { title: 'Giriş yap', subtitle: 'Topluluğa katılmak için hesabına gir.' },
  register: { title: 'Kayıt ol', subtitle: 'Birkaç saniye sürer.' },
  forgot: {
    title: 'Parolamı unuttum',
    subtitle: 'Adresini gir, sıfırlama bağlantısını gönderelim.',
  },
};

/**
 * The sign-in dialog.
 *
 * A modal rather than a page because signing in is an interruption, not a
 * destination: whatever the visitor was reading is still behind it, and they
 * come back to that exact spot rather than to wherever a redirect decided.
 *
 * Everything below the render is what makes it a real dialog rather than a
 * floating div, and each piece is here because of what breaks without it.
 */
export function AuthDialog({ mode, onModeChange, onClose, onAuthenticated }: AuthDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const { title, subtitle } = titles[mode];

  // --- focus, focus restoration, scroll lock -------------------------------
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Scoped to the CONTENT, not the whole panel. querySelector returns the
    // first match in document order, and the sticky header — with its close
    // button — comes first: querying the panel would open every dialog with
    // "Kapat, button" announced and the email field a Tab away.
    const firstField =
      contentRef.current?.querySelector<HTMLElement>(
        'input:not([type="hidden"]), textarea, select',
      ) ?? contentRef.current?.querySelector<HTMLElement>('button, [href]');

    (firstField ?? panelRef.current)?.focus();

    // Locked with `position: fixed` rather than `overflow: hidden`, for the same
    // reason as the story viewer: on iOS the latter does not reliably stop the
    // page behind, and restoring overflow alone loses the reader's place.
    const scrollY = window.scrollY;
    const { position, top, width, overflow } = document.body.style;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.position = position;
      document.body.style.top = top;
      document.body.style.width = width;
      document.body.style.overflow = overflow;
      window.scrollTo(0, scrollY);

      // Without this a keyboard user is dropped at the top of the document
      // rather than back on the button they opened the dialog with.
      previouslyFocused?.focus?.();
    };
    // Deliberately runs once per OPEN, not per mode change: switching between
    // the sign-in and register tabs must not re-lock the body or steal focus
    // back to the first field mid-typing.
  }, []);

  // --- keyboard -------------------------------------------------------------
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();

        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      // Focus trap. Without it Tab walks straight into the page behind, which is
      // still rendered and still focusable — the user ends up typing into a form
      // they cannot see.
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])',
      );

      if (!focusable || focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);

    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  // Portalled to the body so no ancestor's `overflow` or `transform` can clip it
  // — a modal rendered inside a scrolling card is a modal you can scroll away.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      // The backdrop is the click target, and it is a plain div with an
      // onMouseDown rather than a button: a button here would be announced as
      // one, and "close" already has a real button in the corner.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      {/* pointer-events-none is load-bearing. Without it this sibling covers the
          container and IS the target of every backdrop click, so the
          target === currentTarget guard above never passes and tapping outside
          silently does nothing. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-surface-950/80 backdrop-blur-sm"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className={cn(
          'relative flex max-h-[calc(100dvh-1rem)] w-full flex-col overflow-y-auto outline-none',
          // A sheet on a phone, a centred card above `sm`. The safe-area inset
          // keeps the sheet clear of the iOS home indicator.
          'rounded-t-3xl pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:max-w-[460px] sm:rounded-3xl sm:pb-6',
          'auth-panel border border-white/10 shadow-[0_-8px_48px_rgba(0,0,0,0.6)] sm:shadow-[0_8px_48px_rgba(0,0,0,0.6)]',
          // The global reduced-motion rule collapses this to nothing, which is
          // the point: an entrance animation is decoration, and nothing here
          // depends on it having run.
          'motion-safe:animate-[auth-in_350ms_cubic-bezier(0.22,1,0.36,1)]',
        )}
      >
        <div className="auth-panel-header sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-surface-800/70 px-7 pb-5 pt-7 backdrop-blur">
          <div>
            <img src="/gee-logo.png" alt="" width={881} height={519} className="auth-brand" />
            <h2 id={titleId} className="text-2xl font-semibold tracking-tight text-surface-50">
              {title}
            </h2>
            <p id={descriptionId} className="mt-1 text-sm text-surface-400">
              {subtitle}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Kapat"
            className="-mr-2 -mt-1 grid size-11 shrink-0 place-items-center rounded-full text-surface-500 transition hover:bg-surface-800 hover:text-surface-100"
          >
            <svg
              aria-hidden
              viewBox="0 0 24 24"
              className="size-5 fill-none stroke-current"
              strokeWidth={2}
            >
              <path d="m6 6 12 12M18 6 6 18" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div ref={contentRef} className="px-7 pt-6">
          {mode === 'login' ? (
            <LoginForm onAuthenticated={onAuthenticated} onModeChange={onModeChange} />
          ) : mode === 'register' ? (
            <RegisterForm onAuthenticated={onAuthenticated} onModeChange={onModeChange} />
          ) : (
            <ForgotPasswordForm onModeChange={onModeChange} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
